import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Button,
  CircularProgress, Snackbar, Alert, Chip, Divider, Tooltip, Select,
  MenuItem, FormControl, InputLabel, Grid, IconButton, Paper,
  TablePagination, InputAdornment, Stack, Badge,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Save as SaveIcon, Add as AddIcon, Delete as DeleteIcon,
  RefreshOutlined as RefreshIcon,
  Search as SearchIcon, Clear as ClearIcon,
  TargetOutlined, BookOutlined, CalendarToday,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';

/* ─── Design Tokens ──────────────────────────────────────────── */
const C = {
  navy:    '#0D1F3C',
  navyMid: '#1A3460',
  blue:    '#2563EB',
  blueSoft:'#EFF6FF',
  green:   '#16A34A',
  greenSoft:'#F0FDF4',
  amber:   '#D97706',
  amberSoft:'#FFFBEB',
  red:     '#DC2626',
  redSoft: '#FEF2F2',
  border:  '#E2E8F0',
  surface: '#F8FAFD',
  white:   '#FFFFFF',
  text:    '#1E293B',
  muted:   '#64748B',
};

/* ─── Helpers ────────────────────────────────────────────────── */
const fmt = (n) => String(n ?? '').padStart(2, '0');

const StatusBadge = ({ target }) => {
  const cfg = target > 0
    ? { label: `${target} / wk`, bg: C.blueSoft, color: C.blue }
    : { label: 'Not set', bg: '#F1F5F9', color: C.muted };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        bgcolor: cfg.bg, color: cfg.color, fontWeight: 700,
        fontSize: '0.72rem', borderRadius: 1.5, px: 0.5,
      }}
    />
  );
};

const fmtDate = (str) => {
  if (!str) return '—';
  try { return new Date(str).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return str; }
};

/* ─── Main Component ─────────────────────────────────────────── */
export default function SubjectTarget() {
  const [classes,         setClasses]         = useState([]);
  const [sections,        setSections]        = useState([]);
  const [subjects,        setSubjects]        = useState([]);
  const [targets,         setTargets]         = useState([]);
  const [selectedClass,   setSelectedClass]   = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [loading,         setLoading]         = useState(false);
  const [loadingDropdowns,setLoadingDropdowns]= useState(true);
  const [saving,          setSaving]          = useState(false);
  const [snackbar,        setSnackbar]        = useState({ open: false, message: '', severity: 'success' });
  const [editMap,         setEditMap]         = useState({});
  const [newTarget,       setNewTarget]       = useState({ subjectId: '', targetPeriods: 5 });
  const [deleteTarget,    setDeleteTarget]    = useState(null); // { id, subjectName }
  const [search,          setSearch]          = useState('');
  const [page,            setPage]            = useState(0);
  const [rowsPerPage,     setRowsPerPage]     = useState(10);

  // ── Branch state (mirrors Employee.js pattern) ──────────────────────────
  const [isMaster,         setIsMaster]         = useState(false);
  const [branches,         setBranches]         = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // ── Resolve active branchId for use in all API calls ──────────────────
  // Master users: from dropdown selection
  // Non-master users: from sessionStorage (set at login)
  const activeBranchId = isMaster
    ? selectedBranchId
    : sessionStorage.getItem('branchId');

  const snap = (msg, sev = 'success') => setSnackbar({ open: true, message: msg, severity: sev });

  // ── Fetch branch list (master users only) ─────────────────────────────
  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token  = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
        // Auto-fill first branch for master user when branches load
        if (data.length > 0) {
          setSelectedBranchId(data[0].branchId);
        }
      } else {
        console.error('Failed to fetch branches');
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  // ── On mount: detect master user, load branches or read from session ──
  useEffect(() => {
    const isMasterUser = sessionStorage.getItem('is_Master') === 'true';
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      // fetchBranches sets selectedBranchId → triggers the ddlClassName load via selectedBranchId effect
      fetchBranches();
    } else {
      // Non-master: branchId already in session, activeBranchId resolves it directly
    }
  }, []);

  // ── Load classes on mount (non-master) or when branch changes (master) ──
  // Guard: don't fetch until we have a valid branchId
  // APIs integrated on this page:
  //   1. POST /Enquiry/ddlClassName              — no parameters (Swagger: No parameters, empty body)
  //   2. POST /Teacher/ddlSection_clsId          — body: { teacherId, classId } only (Swagger: no branchId in body)
  //   3. POST /ClassMapping/GetSubjectMapping    — body: { classId } only (Swagger: no branchId in body)
  //   4. GET  /adv/subject-target                — Branch_id as query param
  //   5. POST /adv/subject-target/save           — Branch_id as query param, payload array in body
  //   6. DELETE /adv/subject-target/delete/{subTargetId} — Branch_id as query param
  //   7. POST /Branch/GetBranch                  — branch list (master users only)
  useEffect(() => {
    if (!activeBranchId) return;
    // Reset class/section/target state when branch changes
    setSelectedClass('');
    setSelectedSection('');
    setTargets([]);
    setSections([]);
    setSubjects([]);

    (async () => {
      setLoadingDropdowns(true);
      try {
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const token  = sessionStorage.getItem('token');
        // POST /Enquiry/ddlClassName — no parameters (Swagger: No parameters, send empty body)
        const res    = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: token },
          body: JSON.stringify({}),
        });
        setClasses(await res.json().then(d => Array.isArray(d) ? d : []));
      } catch (e) { console.error(e); }
      finally { setLoadingDropdowns(false); }
    })();
  }, [selectedBranchId]); // selectedBranchId change covers master; non-master fires once on mount via activeBranchId

  /* ── load sections + subjects when class changes ── */
  useEffect(() => {
    if (!selectedClass || !activeBranchId) return;
    (async () => {
      try {
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const token  = sessionStorage.getItem('token');
        const [sectRes, subjRes] = await Promise.all([
          // POST /Teacher/ddlSection_clsId — body: { teacherId, classId } only (Swagger: no branchId)
          fetch(`${apiUrl}/Teacher/ddlSection_clsId`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: token },
            body: JSON.stringify({
              teacherId: sessionStorage.getItem('employeeId') || 0,
              classId: selectedClass,
            }),
          }),
          // POST /ClassMapping/GetSubjectMapping — body: { classId } only (Swagger: no branchId)
          fetch(`${apiUrl}/ClassMapping/GetSubjectMapping`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: token },
            body: JSON.stringify({ classId: Number(selectedClass) }),
          }),
        ]);
      setSections(await sectRes.json().then(d => Array.isArray(d) ? d : []));
        setSubjects(
          await subjRes.json().then(d =>
            Array.isArray(d)
              ? d.filter(s => String(s.status ?? s.Status) === '1')
              : []
          )
        );
        setSelectedSection('');
        setTargets([]);
      } catch (e) { console.error(e); }
    })();
  }, [selectedClass]);

  /* ── fetch targets when section selected ── */
  useEffect(() => {
    if (selectedClass && selectedSection) fetchTargets();
  }, [selectedSection]);

  const fetchTargets = async () => {
    if (!activeBranchId) return;
    setLoading(true);
    setPage(0);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token  = sessionStorage.getItem('token');
      // GET /adv/subject-target — classId + sectionId + Branch_id as query params
      const res    = await fetch(
        `${apiUrl}/adv/subject-target?classId=${selectedClass}&sectionId=${selectedSection}&Branch_id=${activeBranchId}`,
        { headers: { Authorization: token } }
      );
      const list = await res.json().then(d => Array.isArray(d) ? d : []);
      setTargets(list);
      const map = {};
      list.forEach(t => { map[t.id] = t.targetPeriods; });
      setEditMap(map);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  /* ── save all ── */
  const handleSaveAll = async () => {
    if (!selectedClass || !selectedSection || !activeBranchId) return;
    setSaving(true);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token  = sessionStorage.getItem('token');
      const payload = targets.map(t => ({
        id: t.id,
        classId: Number(selectedClass),
        sectionId: Number(selectedSection),
        subjectId: t.subjectId,
        targetPeriods: editMap[t.id] ?? t.targetPeriods,
      }));
      // POST /adv/subject-target/save — Branch_id as query param, payload array in body
      const res = await fetch(`${apiUrl}/adv/subject-target/save?Branch_id=${activeBranchId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data?.Status === 'Succeed!' || res.ok) {
        snap('Subject targets saved successfully!');
        fetchTargets();
      } else { snap(data?.Msg || 'Save failed', 'error'); }
    } catch { snap('Error saving targets', 'error'); }
    finally { setSaving(false); }
  };

  /* ── delete ── */
  const confirmDelete = (t) => setDeleteTarget({ id: t.id, subjectName: t.subjectName });

  const handleDelete = async () => {
    if (!deleteTarget || !activeBranchId) return;
    const { id } = deleteTarget;
    setDeleteTarget(null);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token  = sessionStorage.getItem('token');
      // DELETE /adv/subject-target/delete/{id} — Branch_id as query param
      const res    = await fetch(`${apiUrl}/adv/subject-target/delete/${id}?Branch_id=${activeBranchId}`, {
        method: 'DELETE',
        headers: { Authorization: token },
      });
      const data = await res.json();
      if (data?.Status === 'Succeed!' || res.ok) { snap('Target deleted'); fetchTargets(); }
      else { snap(data?.Msg || 'Delete failed', 'error'); }
    } catch { snap('Error deleting target', 'error'); }
  };

  /* ── add new ── */
  const handleAddNew = async () => {
    if (!newTarget.subjectId || !selectedClass || !selectedSection) {
      snap('Please select a subject', 'warning'); return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token  = sessionStorage.getItem('token');
      // POST /adv/subject-target/save — Branch_id as query param, payload array in body
      const res    = await fetch(`${apiUrl}/adv/subject-target/save?Branch_id=${activeBranchId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify([{
          id: 0,
          classId: Number(selectedClass),
          sectionId: Number(selectedSection),
          subjectId: Number(newTarget.subjectId),
          targetPeriods: Number(newTarget.targetPeriods),
        }]),
      });
      const data = await res.json();
      if (data?.Status === 'Succeed!' || res.ok) {
        snap('New subject target added!');
        setNewTarget({ subjectId: '', targetPeriods: 5 });
        fetchTargets();
      } else { snap(data?.Msg || 'Failed to add', 'error'); }
    } catch { snap('Error adding target', 'error'); }
  };

  /* ── filtered + paginated data ── */
  const filtered = useMemo(() => {
    if (!search.trim()) return targets;
    const q = search.toLowerCase();
    return targets.filter(t =>
      (t.subjectName || '').toLowerCase().includes(q) ||
      (t.className || '').toLowerCase().includes(q) ||
      (t.sectionName || '').toLowerCase().includes(q)
    );
  }, [targets, search]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  /* ── summary stats ── */
  const totalPeriods = targets.reduce((s, t) => s + (editMap[t.id] ?? t.targetPeriods), 0);

  /* ────────────────────────────── RENDER ──────────────────────────── */
  return (
    <Box sx={{ bgcolor: C.surface, minHeight: '100vh', pb: 5, fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>

      {/* ── Hero Header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 60%, #1e4d8c 100%)`,
        pt: 4, pb: 3.5, px: 4,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* decorative circles */}
        {[{ size: 220, right: -40, top: -60, op: 0.06 }, { size: 120, right: 140, top: 30, op: 0.04 }].map((c, i) => (
          <Box key={i} sx={{
            position: 'absolute', right: c.right, top: c.top,
            width: c.size, height: c.size, borderRadius: '50%',
            border: `2px solid rgba(255,255,255,${c.op * 5})`,
            bgcolor: `rgba(255,255,255,${c.op})`,
            pointerEvents: 'none',
          }} />
        ))}

        {/* Title row + Branch Dropdown */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <Box sx={{
                bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 2, p: 1,
                display: 'flex', alignItems: 'center',
              }}>
                <TrendingUpIcon sx={{ color: '#93C5FD', fontSize: 22 }} />
              </Box>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, letterSpacing: '-0.5px' }}>
                Subject Weekly Targets
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', ml: 6.5 }}>
              Define and monitor how many periods each subject should have per week
            </Typography>
          </Box>

          {/* ── Branch Dropdown — visible only for Master/Admin users ── */}
          {isMaster && (
            <FormControl size="small" sx={{ width: 220, alignSelf: 'center' }}>
              <Select
                value={selectedBranchId || ''}
                onChange={(e) => {
                  setSelectedBranchId(e.target.value);
                  // search and page reset on branch change is handled by the useEffect above
                  setSearch('');
                  setPage(0);
                }}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', ml: 0.5 }} />
                  </InputAdornment>
                }
                renderValue={(val) => {
                  if (!val) return <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Select Branch</Typography>;
                  const found = branches.find(b => b.branchId === val);
                  return <Typography sx={{ color: '#fff', fontSize: '0.875rem', fontWeight: 500 }}>{found?.branchName || val}</Typography>;
                }}
                sx={{
                  color: '#ffffff',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  letterSpacing: '0.3px',
                  backgroundColor: 'rgba(255,255,255,0.07)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '8px',
                  transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.15)',
                    borderWidth: '1px',
                    transition: 'border-color 0.2s ease',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.11)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.35)' },
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    boxShadow: '0 0 0 3px rgba(100,181,246,0.18)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#64B5F6', borderWidth: '1.5px' },
                  },
                  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)', transition: 'color 0.2s ease' },
                  '&.Mui-focused .MuiSelect-icon': { color: '#64B5F6' },
                  '& .MuiSelect-select': { py: 1.1 },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 0.5, borderRadius: '10px',
                      border: '1px solid rgba(11,31,61,0.12)',
                      boxShadow: '0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)',
                      overflow: 'hidden',
                      '& .MuiList-root': { py: 0.5 },
                    },
                  },
                }}
              >
                {branches.map((branch) => (
                  <MenuItem
                    key={branch.branchId}
                    value={branch.branchId}
                    sx={{
                      fontSize: '0.875rem', fontWeight: 500, color: '#0B1F3D',
                      px: 2, py: 1.1, mx: 0.5, borderRadius: '6px',
                      transition: 'background-color 0.15s ease',
                      '&:hover': { backgroundColor: 'rgba(11,31,61,0.07)' },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(25,118,210,0.1)', color: '#1565C0', fontWeight: 600,
                        '&:hover': { backgroundColor: 'rgba(25,118,210,0.15)' },
                      },
                    }}
                  >
                    {branch.branchName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* quick stats strip */}
        {targets.length > 0 && (
          <Stack direction="row" spacing={2} mt={2.5} ml={0.5}>
            {[
              { label: 'Subjects', value: targets.length, icon: '📚' },
              { label: 'Total Periods / Week', value: totalPeriods, icon: '📅' },
              { label: 'Avg Periods', value: targets.length ? (totalPeriods / targets.length).toFixed(1) : 0, icon: '📊' },
            ].map(s => (
              <Box key={s.label} sx={{
                bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2,
                px: 2, py: 1, backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {s.icon} {s.label}
                </Typography>
                <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 800, lineHeight: 1.3 }}>
                  {s.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>

        {/* ── Filter Card ── */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 16px rgba(11,31,61,0.07)', border: `1px solid ${C.border}`, mb: 3 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: C.navy, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ bgcolor: C.blueSoft, color: C.blue, borderRadius: 1, px: 1, py: 0.3, fontSize: '0.72rem', fontWeight: 800 }}>STEP 1</Box>
              Select Class &amp; Section
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Class</InputLabel>
                  <Select
                    value={selectedClass} label="Class"
                    onChange={e => setSelectedClass(e.target.value)}
                    disabled={loadingDropdowns}
                    sx={{ borderRadius: 2 }}
                  >
                    {classes.map(c => (
                      <MenuItem key={c.ClassId || c.classId} value={c.ClassId || c.classId}>
                        {c.ClassName || c.className}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" disabled={!selectedClass}>
                  <InputLabel>Section</InputLabel>
                  <Select
                    value={selectedSection} label="Section"
                    onChange={e => setSelectedSection(e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    {sections.map(s => (
                      <MenuItem key={s.SectionId || s.sectionId} value={s.SectionId || s.sectionId}>
                        {s.SectionName || s.sectionName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined" startIcon={<RefreshIcon />}
                  onClick={fetchTargets}
                  disabled={!selectedClass || !selectedSection}
                  sx={{ fontWeight: 600, borderRadius: 2, borderColor: C.navy, color: C.navy,
                    '&:hover': { borderColor: C.blue, color: C.blue, bgcolor: C.blueSoft } }}
                >
                  Reload Data
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 8, gap: 2 }}>
            <CircularProgress sx={{ color: C.navy }} thickness={4} />
            <Typography color="text.secondary" variant="body2">Loading subject targets…</Typography>
          </Box>
        ) : selectedClass && selectedSection ? (
          <>
            {/* ── Add New Target ── */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 16px rgba(11,31,61,0.07)', border: `1px solid ${C.border}`, mb: 3 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: C.navy, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="span" sx={{ bgcolor: C.greenSoft, color: C.green, borderRadius: 1, px: 1, py: 0.3, fontSize: '0.72rem', fontWeight: 800 }}>STEP 2</Box>
                  Add New Subject Target
                </Typography>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Subject</InputLabel>
                      <Select
                        value={newTarget.subjectId} label="Subject"
                        onChange={e => setNewTarget(n => ({ ...n, subjectId: e.target.value }))}
                        sx={{ borderRadius: 2 }}
                      >
                        {subjects.map(s => (
                          <MenuItem key={s.SubjectId || s.subjectId} value={s.SubjectId || s.subjectId}>
                            {s.SubjectName || s.subjectName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      size="small" fullWidth label="Target Periods / Week"
                      type="number"
                      value={newTarget.targetPeriods}
                      onChange={e => setNewTarget(n => ({ ...n, targetPeriods: e.target.value }))}
                      inputProps={{ min: 1, max: 40 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained" startIcon={<AddIcon />}
                      onClick={handleAddNew} fullWidth
                      sx={{
                        bgcolor: C.green, fontWeight: 700, borderRadius: 2, py: 1,
                        boxShadow: `0 4px 12px ${C.green}40`,
                        '&:hover': { bgcolor: '#15803D', boxShadow: `0 6px 16px ${C.green}50` },
                      }}
                    >
                      Add Target
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Targets Table Card ── */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 16px rgba(11,31,61,0.07)', border: `1px solid ${C.border}` }}>

              {/* table header toolbar */}
              <Box sx={{
                display: 'flex', flexWrap: 'wrap', gap: 2,
                justifyContent: 'space-between', alignItems: 'center',
                p: 2.5, pb: 2,
              }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.navy }}>
                    Subject Targets
                    <Badge
                      badgeContent={targets.length} color="primary"
                      sx={{ ml: 1.5, '& .MuiBadge-badge': { fontSize: '0.65rem', fontWeight: 800 } }}
                    />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filtered.length !== targets.length
                      ? `Showing ${filtered.length} of ${targets.length} subjects`
                      : `${targets.length} subjects configured`}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                  {/* search */}
                  <TextField
                    size="small" placeholder="Search subjects…"
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(0); }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: C.muted }} /></InputAdornment>,
                      endAdornment: search ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearch('')}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />

                  <Button
                    variant="contained" startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
                    onClick={handleSaveAll}
                    disabled={saving || targets.length === 0}
                    sx={{
                      bgcolor: C.navy, fontWeight: 700, borderRadius: 2,
                      boxShadow: `0 4px 12px ${C.navy}30`,
                      '&:hover': { bgcolor: C.navyMid },
                    }}
                  >
                    {saving ? 'Saving…' : 'Save Changes'}
                  </Button>
                </Stack>
              </Box>

              <Divider />

              {/* table */}
              {targets.length === 0 ? (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>📋</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: C.navy, mb: 0.5 }}>No targets configured</Typography>
                  <Typography color="text.secondary" variant="body2">
                    Add a subject target using the form above to get started.
                  </Typography>
                </Box>
              ) : filtered.length === 0 ? (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '2rem', mb: 1 }}>🔍</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: C.navy, mb: 0.5 }}>No results found</Typography>
                  <Typography color="text.secondary" variant="body2">
                    Try a different search term.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: '#F1F5F9', fontWeight: 700, fontSize: '0.75rem',
                        color: C.muted, py: 1.5, textTransform: 'uppercase', letterSpacing: 0.4,
                      },
                      '& .MuiTableRow-root:hover': { bgcolor: C.blueSoft },
                    }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ pl: 3 }}>#</TableCell>
                          <TableCell>Subject Name</TableCell>
                          <TableCell align="center">Class &amp; Section</TableCell>
                          <TableCell align="center">Target Periods / Week</TableCell>
                          <TableCell align="center">Current Target</TableCell>
                          <TableCell align="center">Created On</TableCell>
                          <TableCell align="center">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginated.map((t, idx) => (
                          <TableRow key={t.id} sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell sx={{ pl: 3 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: C.muted }}>
                                {page * rowsPerPage + idx + 1}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1.2}>
                                <Box sx={{
                                  width: 34, height: 34, borderRadius: 2,
                                  bgcolor: `hsl(${(t.subjectId * 47) % 360}, 60%, 92%)`,
                                  color:   `hsl(${(t.subjectId * 47) % 360}, 55%, 35%)`,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontWeight: 800, fontSize: '0.8rem', flexShrink: 0,
                                }}>
                                  {(t.subjectName || 'S')[0].toUpperCase()}
                                </Box>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: C.text }}>
                                    {t.subjectName || `Subject ${t.subjectId}`}
                                  </Typography>
                                  
                                </Box>
                              </Stack>
                            </TableCell>

                            <TableCell align="center">
                              <Stack direction="row" spacing={0.7} justifyContent="center">
                                <Chip label={t.className}   size="small" sx={{ bgcolor: '#F0F9FF', color: '#0369A1', fontWeight: 700, fontSize: '0.7rem', borderRadius: 1 }} />
                                <Chip label={t.sectionName} size="small" sx={{ bgcolor: '#FDF4FF', color: '#7E22CE', fontWeight: 700, fontSize: '0.7rem', borderRadius: 1 }} />
                              </Stack>
                            </TableCell>

                            <TableCell align="center">
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <TextField
                                  type="number" size="small"
                                  value={editMap[t.id] ?? t.targetPeriods}
                                  onChange={e => setEditMap(m => ({ ...m, [t.id]: Number(e.target.value) }))}
                                  inputProps={{ min: 1, max: 40, style: { textAlign: 'center', fontWeight: 800, width: 52 } }}
                                  sx={{
                                    width: 82,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      '&:hover fieldset': { borderColor: C.blue },
                                      '&.Mui-focused fieldset': { borderColor: C.blue },
                                    },
                                  }}
                                />
                              </Box>
                            </TableCell>

                            <TableCell align="center">
                              <StatusBadge target={t.targetPeriods} />
                            </TableCell>

                            <TableCell align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                                {fmtDate(t.createdDate)}
                              </Typography>
                            </TableCell>

                            <TableCell align="center">
                              <Tooltip title="Delete this target">
                                <IconButton
                                  size="small"
                                  onClick={() => confirmDelete(t)}
                                  sx={{
                                    color: C.red, bgcolor: C.redSoft, borderRadius: 1.5,
                                    '&:hover': { bgcolor: '#FEE2E2' },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* pagination */}
                  <Divider />
                  <TablePagination
                    component="div"
                    count={filtered.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Rows per page:"
                    sx={{
                      '& .MuiTablePagination-toolbar': { px: 2 },
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontWeight: 600, fontSize: '0.8rem',
                      },
                    }}
                  />
                </>
              )}
            </Card>
          </>
        ) : (
          /* empty state */
          <Paper sx={{
            p: 8, textAlign: 'center', borderRadius: 3,
            border: `2px dashed ${C.border}`, bgcolor: C.white, boxShadow: 'none',
          }}>
            <Typography sx={{ fontSize: '3rem', mb: 1.5 }}>🏫</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: C.navy, mb: 0.5 }}>
              Get Started
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Select a <strong>Class</strong> and <strong>Section</strong> above to view and manage subject targets.
            </Typography>
          </Paper>
        )}
      </Container>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 400 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: C.navy, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ bgcolor: C.redSoft, borderRadius: 2, p: 1, display: 'flex' }}>
            <DeleteIcon sx={{ color: C.red, fontSize: 22 }} />
          </Box>
          Delete Subject Target
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            Are you sure you want to delete the target for{' '}
            <Typography component="span" sx={{ fontWeight: 700, color: C.text }}>
              {deleteTarget?.subjectName || 'this subject'}
            </Typography>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined" onClick={() => setDeleteTarget(null)}
            sx={{ fontWeight: 600, borderRadius: 2, borderColor: C.border, color: C.muted,
              '&:hover': { borderColor: C.navy, color: C.navy } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained" onClick={handleDelete}
            startIcon={<DeleteIcon />}
            sx={{ fontWeight: 700, borderRadius: 2, bgcolor: C.red,
              boxShadow: `0 4px 12px ${C.red}35`,
              '&:hover': { bgcolor: '#B91C1C' } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* snackbar */}
      <Snackbar
        open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity} variant="filled"
          sx={{ width: '100%', borderRadius: 2, fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}